import React from "react";
import { CompactTextInput } from "newui/components/ui/InputWithLabel/CompactTextInput";
import type { CheckinAttendee } from "./types";

interface CheckinRowProps {
  attendee: CheckinAttendee;
  commentValue: string;
  isDisabled?: boolean;
  onCommentBlur: (attendeeId: number) => void;
  onCommentChange: (attendeeId: number, comment: string) => void;
  onStatusChange: (
    attendeeId: number,
    status: "attending" | "not_attending"
  ) => void;
}

export const CheckinRow: React.FC<CheckinRowProps> = ({
  attendee,
  commentValue,
  onStatusChange,
  onCommentChange,
  onCommentBlur,
  isDisabled = false,
}) => {
  const isAttending = attendee.attendance_status === "attending";

  const handleToggle = () => {
    const next = isAttending ? "not_attending" : "attending";
    onStatusChange(attendee.id, next);
  };

  const statusLabel =
    attendee.attendance_status === "pending"
      ? "Pending"
      : isAttending
      ? "Attending"
      : "Not Attending";

  const statusColor = isAttending
    ? "text-green-600"
    : "text-gray-400";

  return (
    <div
      className="py-3 border-b border-gray-100 last:border-b-0"
      data-testid={`checkin-row-${attendee.id}`}
    >
      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          checked={isAttending}
          onChange={handleToggle}
          disabled={isDisabled}
          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
          data-testid={`checkin-checkbox-${attendee.id}`}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-gray-900 truncate">
              {attendee.name}
            </p>
            {attendee.badges?.map((badge) => (
              <span
                key={badge}
                className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700"
              >
                {badge}
              </span>
            ))}
          </div>
          <p className="text-xs text-gray-500 truncate">{attendee.email}</p>
        </div>
        <div className="min-w-[120px] text-right">
          <p className={`text-xs font-medium ${statusColor}`}>
            {statusLabel}
          </p>
        </div>
      </div>
      <div className="mt-2 pl-7">
        <CompactTextInput
          placeholder="Add a comment (optional)"
          value={commentValue}
          onChange={(e) => onCommentChange(attendee.id, e.target.value)}
          onBlur={() => onCommentBlur(attendee.id)}
          disabled={isDisabled}
          data-testid={`checkin-comment-${attendee.id}`}
        />
      </div>
    </div>
  );
};
